import { Button, UIHeading5, UIText3 } from '@cian/ui-kit';
import { useState, useMemo, useCallback, MouseEvent } from 'react';

import { ContentRow } from '../../components/ContentRow';
import { useApplicationContext } from '../../context/applicationContext';
import { IPhone } from '../../types';
import { stopPropagation } from '../../utils/event';
import { useOffer, useTracking } from '../../hooks';
import { getDynamicCalltrackingPhone } from '../../services/getDynamicCalltrackingPhone';

const EMPTY_PHONES = [{ countryCode: null, number: null }];

export function AgentPhoneButtonContainer() {
  const {
    phones: offerPhones,
    showWarningMessage,
    newbuildingDynamicCalltracking,
    id,
    isCianPartner,
    user,
  } = useOffer();
  const { onPhoneShow } = useTracking();
  const agentWorkTime = user?.agentAvailability?.title;

  const { logger, httpApi } = useApplicationContext();

  const [opened, setOpened] = useState(false);
  // dynamic calltracking
  const [comagicLoaded, setComagicLoaded] = useState(false);
  const [comagicLoading, setComagicLoading] = useState(false);
  const [comagicPhone, setComagicPhone] = useState<IPhone | null>(null);

  const phones: IPhone[] = useMemo(() => {
    if (!comagicLoaded || !offerPhones) {
      return EMPTY_PHONES;
    }

    return comagicPhone ? [comagicPhone] : offerPhones;
  }, [comagicLoaded, comagicPhone, offerPhones]);

  const handleClick = useCallback(
    async (e: MouseEvent<HTMLElement>) => {
      stopPropagation(e);
      onPhoneShow();
      const siteBlockId = newbuildingDynamicCalltracking && newbuildingDynamicCalltracking.siteBlockId;
      setComagicLoading(true);
      if (siteBlockId) {
        try {
          const { phone } = await getDynamicCalltrackingPhone({
            httpApi,
            logger,
            blockId: siteBlockId,
            announcementId: id,
            placeType: 'offer',
            dynamicCalltrackingPlaceType: 'sidePanel',
          });

          if (phone) {
            setComagicPhone({
              countryCode: phone[0],
              number: phone.slice(1),
            });
          }
          // eslint-disable-next-line no-empty
        } catch (err) {}
      }

      setComagicLoading(false);
      setComagicLoaded(true);
      setOpened(true);
    },
    [onPhoneShow, newbuildingDynamicCalltracking, httpApi, logger, id],
  );

  const slicedPhone = useMemo(() => {
    return offerPhones && offerPhones[0] ? `${formatPhone(offerPhones[0]).slice(0, -2)}...` : '';
  }, [offerPhones]);

  if (isCianPartner) {
    return null;
  }

  if (!opened) {
    return (
      <Button
        data-name="PhoneButton"
        fullWidth
        size="XS"
        onClick={handleClick}
        theme="fill_primary"
        loading={comagicLoading}
      >
        {slicedPhone}
      </Button>
    );
  }

  return (
    <>
      <ContentRow>
        {phones.map((phone, index) => (
          <ContentRow key={index}>
            <UIHeading5 data-mark="PhoneValue" onClick={stopPropagation}>
              {formatPhone(phone)}
            </UIHeading5>
            {index === phones.length - 1 && agentWorkTime && <UIText3 color="gray60_100">{agentWorkTime}</UIText3>}
          </ContentRow>
        ))}
      </ContentRow>
      {showWarningMessage && (
        <ContentRow>
          <UIText3 data-mark="new-agent-warning" color="gray60_100" whiteSpace="pre-line">
            Не вносите предоплату до просмотра объекта и заключения договора!
          </UIText3>
        </ContentRow>
      )}
    </>
  );
}

export function formatPhone(phone: IPhone): string {
  if (!phone || !phone.number) {
    return '';
  }

  return (
    `${phone.countryCode && phone.countryCode.startsWith('+') ? '' : '+'}${phone.countryCode} ` +
    `${phone.number.substr(0, 3)} ` +
    `${phone.number.substr(3, 3)}-${phone.number.substr(6, 2)}-${phone.number.substr(8, 2)}`
  );
}
